/**
 * Report filters — declaration, URL serialization, and zod validation.
 *
 * CLAUDE.md §4.4: **filter state lives in the URL**, never `useState` alone and never
 * `localStorage`. A report declares its filters as data — a `FilterSchema` in
 * `reports/<id>/filters.ts` — and the params it owns, their validation, what `<FilterBar>` renders
 * and what reaches `/query` all follow from that.
 *
 * Deliberately React-free; the router binding is `components/report/useReportFilters.ts`.
 * Why it is shaped the way it is: `src/lib/design-notes.md`.
 */

import { format, isValid, parseISO } from 'date-fns';
import { z } from 'zod';

/** One choice in a single-select filter. */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * A filter whose choices are read from its own source table, never hardcoded here (§5.4). Declared
 * in the report's `report.yaml` under `options:`; build one with {@link liveOptionsFor}.
 */
export interface LiveOptions {
  readonly live: true;
  readonly report: string;
  readonly filter: string;
}

/** Either the choices themselves, or where to go and get them. */
export type OptionSource = readonly FilterOption[] | LiveOptions;

/** The live-options helper, curried on the report id: `const live = liveOptionsFor('mo-list')`. */
export function liveOptionsFor(report: string): (filter: string) => LiveOptions {
  return (filter: string) => ({ live: true, report, filter });
}

/** Whether this field's choices still have to be fetched. */
export function isLive(source: OptionSource): source is LiveOptions {
  return !Array.isArray(source) && (source as LiveOptions).live === true;
}

/** The choices, or an empty list while they are still on their way. */
export function optionsOf(source: OptionSource): readonly FilterOption[] {
  return isLive(source) ? [] : source;
}

interface FilterFieldBase {
  /** Identifier for the field. For every type except `date-range` this is also the URL param. */
  name: string;
  /** The visible label. Name things the way factory users say them, not the way ADB spells them (§4.10). */
  label: string;
  /** No sensible "all" answer. Keeps `ParsedFilters.isComplete` false until it has a value. */
  required?: boolean;
  /** Optional one-line help shown under the control. */
  hint?: string;
}

export interface TextFilterField extends FilterFieldBase {
  type: 'text';
  placeholder?: string;
  defaultValue?: string;
}

export interface SelectFilterField extends FilterFieldBase {
  type: 'select';
  options: OptionSource;
  /** Render a compact button group instead of the default drop-down. */
  presentation?: 'dropdown' | 'segmented';
  /** Label for the "no choice made" option. Defaults to "All". */
  anyLabel?: string;
  /**
   * Fixed value, or a function resolved on each parse (e.g. login default factory that may
   * arrive after the module first loads).
   */
  defaultValue?: FilterDefault<string>;
}

/**
 * Multi-value select. The URL wire format is a comma-separated list (`?brand=NIKE,ADIDAS`),
 * matching the server's multi-value binder (§5.4).
 */
export interface MultiSelectFilterField extends FilterFieldBase {
  type: 'multi-select';
  options: OptionSource;
  /** Label when nothing is selected. Defaults to "All". */
  anyLabel?: string;
  /** Comma-separated defaults, or a function returning one. */
  defaultValue?: FilterDefault<string>;
}

/**
 * A two-state toggle for a server-declared `type: boolean` filter — always on or always off, never
 * absent. Not a two-option `select`: a select can be cleared to "no choice" and a boolean predicate
 * has no such state, so `defaultValue` is mandatory here (§5.4 — bind every declared parameter).
 */
export interface SwitchFilterField extends FilterFieldBase {
  type: 'switch';
  /** What the URL and the API carry in each state. Defaults to `'true'` / `'false'`. */
  onValue?: string;
  offValue?: string;
  /** Which state the report opens in. Required — see above. */
  defaultValue: FilterDefault<string>;
  /** What the state *means* in the reader's words (§4.10): "Included"/"Excluded", not "On"/"Off". */
  onLabel?: string;
  offLabel?: string;
}

/** The wire values of a switch, with the defaults applied. */
export function switchValues(field: SwitchFilterField): { on: string; off: string } {
  return { on: field.onValue ?? 'true', off: field.offValue ?? 'false' };
}

/**
 * A default that may be computed rather than fixed. Resolved on each parse, not at module load: a
 * tab left open on a factory floor across midnight must not keep yesterday's idea of "current".
 */
export type FilterDefault<TValue> = TValue | (() => TValue);

function resolveDefault<TValue>(value: FilterDefault<TValue> | undefined): TValue | null {
  if (value === undefined) {
    return null;
  }
  return typeof value === 'function' ? (value as () => TValue)() : value;
}

export interface DateFilterField extends FilterFieldBase {
  type: 'date';
  /** `yyyy-MM-dd`, or a function returning one. */
  defaultValue?: FilterDefault<string>;
}

/** A from/to pair, owning two URL params — what the server binds as two named parameters (§5.4). */
export interface DateRangeFilterField extends FilterFieldBase {
  type: 'date-range';
  fromParam: string;
  toParam: string;
  /** Both `yyyy-MM-dd`, or a function returning the pair. */
  defaultValue?: FilterDefault<{ from: string; to: string }>;
}

export type FilterField =
  | TextFilterField
  | SelectFilterField
  | MultiSelectFilterField
  | SwitchFilterField
  | DateFilterField
  | DateRangeFilterField;

/** Split a multi-select URL value into ordered unique tokens. */
export function decodeMultiValue(raw: string | null | undefined): string[] {
  if (raw === null || raw === undefined || raw.trim() === '') {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const token = part.trim();
    if (token === '' || seen.has(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
}

/** Join selected values for the URL / API. Empty selection becomes null (param omitted). */
export function encodeMultiValue(values: readonly string[]): string | null {
  const cleaned = values.map((v) => v.trim()).filter((v) => v !== '');
  return cleaned.length === 0 ? null : cleaned.join(',');
}

export type FilterSchema = readonly FilterField[];

/**
 * Filter values, keyed by URL param name — the exact shape `/query` takes. Every declared param is
 * present on every parse, `null` when absent, mirroring §5.4: the null-guard predicate on the
 * server only works if the parameter is always supplied.
 */
export type FilterValues = Record<string, string | null>;

export interface FilterIssue {
  /** The URL param that could not be used. */
  param: string;
  label: string;
  message: string;
}

export interface ParsedFilters {
  values: FilterValues;
  /** What could not be read. A bad param never throws — a mistyped link lands on a usable report. */
  issues: FilterIssue[];
  /** False while a required filter is missing or a range runs backwards. Gate the query on it. */
  isComplete: boolean;
}

/**
 * Sentinel for "no choice" / "all": Radix Select cannot hold an empty string, and an optional
 * select with a `defaultValue` writes this into the URL so the next parse does not re-apply the
 * default. Explicit "all" and "never set" are different states and the URL must say which.
 */
export const ANY_VALUE = '__any__';

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Dates look like 2026-01-31. Pick one from the calendar.')
  .refine((value) => isValid(parseISO(value)), 'That date does not exist.');

const freeText = z.string().trim().min(1);

/** The shape of a multi-select URL value, with no claim about which tokens are real choices. */
const multiValueShape = z
  .string()
  .refine((value) => decodeMultiValue(value).length > 0, 'That value could not be read.');

function validatorFor(field: FilterField): z.ZodType<string> {
  switch (field.type) {
    case 'date':
    case 'date-range':
      return isoDate;
    case 'select':
      // Load-bearing: `readParam` DISCARDS what it cannot validate, so checking against options
      // that have not arrived would turn `?brand=NIKE` into "all brands" and break §4.4. Accept
      // provisionally; the schema is rebuilt and re-checked once the options land.
      if (isLive(field.options)) {
        return freeText;
      }
      return z
        .string()
        .refine(
          (value) => optionsOf(field.options).some((option) => option.value === value),
          'That is not one of the available choices.',
        );
    case 'multi-select': {
      if (isLive(field.options)) {
        return multiValueShape;
      }
      const allowed = new Set(optionsOf(field.options).map((option) => option.value));
      return z.string().refine((value) => {
        const parts = decodeMultiValue(value);
        return parts.length > 0 && parts.every((part) => allowed.has(part));
      }, 'That is not one of the available choices.');
    }
    case 'switch': {
      const { on, off } = switchValues(field);
      return z
        .string()
        .refine((value) => value === on || value === off, 'That is not one of the two settings.');
    }
    case 'text':
      return freeText;
  }
}

/** The URL params a field owns. Everything else in the query string belongs to somebody else. */
export function filterParams(field: FilterField): string[] {
  return field.type === 'date-range' ? [field.fromParam, field.toParam] : [field.name];
}

/** Every URL param the schema owns, in declaration order. */
export function schemaParams(schema: FilterSchema): string[] {
  return schema.flatMap(filterParams);
}

function readParam(
  search: URLSearchParams,
  param: string,
  label: string,
  validator: z.ZodType<string>,
  fallback: string | null,
  issues: FilterIssue[],
): string | null {
  const raw = search.get(param);
  if (raw === null || raw.trim() === '') {
    return fallback;
  }

  const result = validator.safeParse(raw);
  if (result.success) {
    return result.data;
  }

  issues.push({
    param,
    label,
    message: result.error.issues[0]?.message ?? 'That value could not be read.',
  });
  return fallback;
}

/**
 * Read the URL into filter values, validating each param against its declaration. Per-param, so
 * one unreadable value degrades to its default instead of discarding the rest of the link.
 */
export function parseFilters(schema: FilterSchema, search: URLSearchParams): ParsedFilters {
  const values: FilterValues = {};
  const issues: FilterIssue[] = [];
  let isComplete = true;

  for (const field of schema) {
    const validator = validatorFor(field);

    if (field.type === 'date-range') {
      // Resolved once, so both ends come from the same instant and cannot straddle midnight.
      const fallback = resolveDefault(field.defaultValue);
      const from = readParam(
        search,
        field.fromParam,
        `${field.label} (from)`,
        validator,
        fallback?.from ?? null,
        issues,
      );
      const to = readParam(
        search,
        field.toParam,
        `${field.label} (to)`,
        validator,
        fallback?.to ?? null,
        issues,
      );

      values[field.fromParam] = from;
      values[field.toParam] = to;

      if (field.required === true && (from === null || to === null)) {
        isComplete = false;
      }
      // ISO dates compare correctly as strings.
      if (from !== null && to !== null && from > to) {
        issues.push({
          param: field.toParam,
          label: field.label,
          message: 'The end date is before the start date. Swap them to see results.',
        });
        isComplete = false;
      }
      continue;
    }

    // Optional select with a default: `?factory=__any__` means the reader chose "All", not
    // "apply the default". A missing param still falls through to the default (first open).
    if (
      field.type === 'select' &&
      field.required !== true &&
      search.get(field.name) === ANY_VALUE
    ) {
      values[field.name] = null;
      continue;
    }

    const value = readParam(
      search,
      field.name,
      field.label,
      validator,
      resolveDefault(field.defaultValue),
      issues,
    );
    values[field.name] = value;

    if (field.required === true && value === null) {
      isComplete = false;
    }
  }

  return { values, issues, isComplete };
}

/**
 * Apply changed values to a query string, returning a new one. Params the schema does not own are
 * carried through untouched, and `null` removes a param so a shared link carries only what is set.
 */
export function applyFilterValues(
  schema: FilterSchema,
  search: URLSearchParams,
  changes: Partial<FilterValues>,
): URLSearchParams {
  const owned = new Set(schemaParams(schema));
  const next = new URLSearchParams(search);
  const fieldByParam = new Map<string, FilterField>();
  for (const field of schema) {
    for (const param of filterParams(field)) {
      fieldByParam.set(param, field);
    }
  }

  for (const [param, value] of Object.entries(changes)) {
    if (!owned.has(param)) {
      continue;
    }
    if (value === null || value === undefined || value === '') {
      const field = fieldByParam.get(param);
      // Keep "All" visible in the URL when a select has a default that would otherwise snap back.
      if (
        field?.type === 'select' &&
        field.required !== true &&
        field.defaultValue !== undefined
      ) {
        next.set(param, ANY_VALUE);
      } else {
        next.delete(param);
      }
    } else if (value === ANY_VALUE) {
      next.set(param, ANY_VALUE);
    } else {
      next.set(param, value);
    }
  }

  return next;
}

/** Drop every filter the schema owns, leaving the rest of the query string alone. */
export function clearFilterValues(
  schema: FilterSchema,
  search: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(search);
  for (const param of schemaParams(schema)) {
    next.delete(param);
  }
  return next;
}

/** One filter's current setting, named and valued the way the reader sees it in `<FilterBar>`. */
export interface FilterFacet {
  /** The URL param, so two instances of a report can be compared field by field. */
  param: string;
  label: string;
  value: string;
}

/** Dates are short in a tab strip; the year is dropped because the report title is not. */
function shortDate(value: string): string {
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'd MMM') : value;
}

function optionLabel(source: OptionSource, value: string): string {
  return optionsOf(source).find((option) => option.value === value)?.label ?? value;
}

/**
 * Describe what a report's filters are currently set to, in the reader's words — option labels
 * rather than the codes the URL carries, which is why this takes the *resolved* schema. Only
 * filters that are set appear. Used to tell two open instances of one report apart.
 */
export function describeFilters(schema: FilterSchema, values: FilterValues): FilterFacet[] {
  const facets: FilterFacet[] = [];

  for (const field of schema) {
    if (field.type === 'date-range') {
      const from = values[field.fromParam];
      const to = values[field.toParam];
      if (from === null || from === undefined || to === null || to === undefined) continue;
      facets.push({
        param: field.fromParam,
        label: field.label,
        value: `${shortDate(from)} – ${shortDate(to)}`,
      });
      continue;
    }

    const value = values[field.name];
    if (value === null || value === undefined || value === '') continue;

    switch (field.type) {
      case 'select':
        facets.push({ param: field.name, label: field.label, value: optionLabel(field.options, value) });
        break;
      case 'multi-select': {
        const tokens = decodeMultiValue(value);
        const named = tokens.slice(0, 2).map((token) => optionLabel(field.options, token));
        const rest = tokens.length - named.length;
        facets.push({
          param: field.name,
          label: field.label,
          value: rest > 0 ? `${named.join(', ')} +${String(rest)}` : named.join(', '),
        });
        break;
      }
      case 'switch': {
        const { on } = switchValues(field);
        facets.push({
          param: field.name,
          label: field.label,
          value: value === on ? (field.onLabel ?? 'On') : (field.offLabel ?? 'Off'),
        });
        break;
      }
      case 'date':
        facets.push({ param: field.name, label: field.label, value: shortDate(value) });
        break;
      case 'text':
        facets.push({ param: field.name, label: field.label, value });
        break;
    }
  }

  return facets;
}

/** True when the URL sets at least one of the schema's params — what "Clear filters" acts on. */
export function hasActiveFilters(schema: FilterSchema, search: URLSearchParams): boolean {
  return schemaParams(schema).some((param) => {
    const raw = search.get(param);
    return raw !== null && raw.trim() !== '';
  });
}
